import React, {useEffect, useState} from 'react';
import {Box, Button, Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {finalize} from 'rxjs';

import {executionsService, ExecutionStatus, ManageExecution} from '@store/operations/executions';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import ExecutionCard from '@screens/admin/operations/executions/manageExecution/components/Execution.card';

import {Close} from '@icons/index';
import {Colors} from '@constants/colors.constants';
import ReminderEmailModal from '@screens/admin/operations/executions/manageExecution/modal/SendReminderEmailModal';

interface ManageExecutionDrawerProps {
  isCreation?: boolean;
  readOnly?: boolean;
}

interface LocationState {
  parentId?: number;
}

const ManageExecutionDrawer = (props: ManageExecutionDrawerProps) => {
  const {isCreation, readOnly} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {executionId} = useParams();
  const location: LocationState = useLocation().state as LocationState;
  const [reminderEmailModalOpen, setReminderEmailModalOpen] = useState(false);

  const parentId = location?.parentId;

  const [loading, setLoading] = useState(false);

  const [executions, setExecutions] = useState<ManageExecution[]>(
    isCreation ? [{isNew: true, quantity1: 1, parentExecutionId: parentId}] : []
  );

  useEffect(() => {
    if (!isCreation && executionId) {
      executionsService.getExecutionById(executionId).subscribe({
        next: setExecutions,
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, executionId, isCreation]);

  const updateExecution = (index: number) => (data: ManageExecution, field?: string) => {
    let newExecutions = [...executions];

    if (index === 0) {
      let keysToDelete: string[] = [];

      if (field === 'organisation') keysToDelete = ['organisationUnit', 'service', 'serviceKind', 'waste', 'container'];
      if (field === 'organisationUnit') keysToDelete = ['service', 'serviceKind', 'waste', 'container'];
      if (field === 'serviceKind') keysToDelete = ['service', 'waste', 'container'];

      newExecutions = newExecutions.map((execution, index) => {
        if (index === 0) return execution;

        let newExecution = {...execution};

        keysToDelete.forEach((key) => delete newExecution[key]);

        if (field && ['organisationUnit', 'serviceKind'].includes(field)) newExecution = {...newExecution, ...data};

        if (field === 'plannedDate') {
          newExecution.plannedFrom = data.plannedFrom;
          newExecution.plannedTo = data.plannedTo;
        }

        if (field === 'plannedFrom') newExecution.plannedFrom = data.plannedFrom;
        if (field === 'plannedTo') newExecution.plannedTo = data.plannedTo;

        return newExecution;
      });

      keysToDelete.forEach((key) => delete newExecutions[0][key]);
    }

    newExecutions[index] = {
      ...newExecutions[index],
      ...data,
    };

    setExecutions(newExecutions);
  };

  const handleClose = () => navigate(-1);

  const handleSave = () => {
    setLoading(true);
    if (executions.some((e) => {
      if (e.distributionRules && e.distributionRules.length > 0) {
        const total = Math.round(e.distributionRules.reduce((acc, dr) => acc + (dr.rateInPercent || 0), 0) * 100) / 100;
        if (total != 100) {
          return true;
        }
      }
      return false;
    })) {
      enqueueSnackbar(t('operations.executions.manage.error.distributionRules'), {variant: 'error'});
      setLoading(false);
      return;
    }
    executionsService
      .saveExecutions(isCreation, executions)
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        next: ([created, updated, deleted, newExecutions]) => {
          if (created > 0)
            enqueueSnackbar(t('operations.executions.success.create', {count: created}), {variant: 'success'});
          if (updated > 0)
            enqueueSnackbar(t('operations.executions.success.update', {count: updated}), {variant: 'success'});
          // navigate(`../${newExecutions[0].id}`);
          setExecutions(newExecutions);
          if (parentId)
            executionsService.getLinkedExecutionById(parentId).subscribe({
              error: (err) => enqueueSnackbar(err.text, err.options),
            });
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const handleDeleteExecution = (index: ManageExecution['id']) => () => {
    if (isCreation) {
      setExecutions(executions.filter((e, eIndex) => eIndex !== index));
    }
  };

  const errors = executions.map((execution, index) => ({
    organisation:
      index === 0 ? getFieldError(execution.organisation, FieldValidationType.REQUIRED_SELECT_ITEM) : undefined,
    organisationUnit: getFieldError(execution.organisationUnit, FieldValidationType.REQUIRED_SELECT_ITEM),
    executedAt: index === 0 ? getFieldError(execution.executedAt, FieldValidationType.DATE) : undefined,
    plannedDate: index === 0 && execution.status != ExecutionStatus.CREATED ? getFieldError(execution.plannedDate, FieldValidationType.REQUIRED_DATE) : undefined,
    plannedFrom: index === 0 && execution.status != ExecutionStatus.CREATED ? getFieldError(execution.plannedFrom, FieldValidationType.REQUIRED_TIME) : undefined,
    plannedTo: index === 0 && execution.status != ExecutionStatus.CREATED ? getFieldError(execution.plannedTo, FieldValidationType.REQUIRED_TIME) : undefined,
    service: getFieldError(execution.service, FieldValidationType.REQUIRED_NUMBER),
    quantity1: getFieldError(execution.quantity1, FieldValidationType.REQUIRED_NUMBER),
    status: getFieldError(execution.status, FieldValidationType.REQUIRED_STRING),
    distributionRules: execution.distributionRules?.map((dr) => ({
      unit: getFieldError(dr.clientOrganisationUnit, FieldValidationType.REQUIRED_SELECT_ITEM),
      rateInPercent: getFieldError(dr.rateInPercent, FieldValidationType.REQUIRED_NUMBER),
    })),
  }));

  return (
    <>
      <Drawer anchor="right" open onClose={handleClose}>
        <Stack overflow="hidden">
          <Stack direction="row" justifyContent="flex-end">
            <IconButton onClick={handleClose}>
              <Close/>
            </IconButton>
          </Stack>
          <Stack pr="10px">
            <Stack direction="row" alignItems="center" mb="15px" spacing={1.5}>
              <Typography fontSize={24} fontWeight="700">
                {isCreation ? t('operations.executions.manage.title.add') : executions?.[0]?.name}
              </Typography>
              {!isCreation && !!executions.length && executions[0].status && (
                <Box bgcolor={Colors.background} p="8px">
                  <Typography textAlign="center" textTransform="uppercase">
                    {ExecutionStatus.selectItems().find((s) => s.value === executions[0].status)?.label || ''}
                  </Typography>
                </Box>
              )}
            </Stack>
            <Divider variant="fullWidth" orientation="horizontal"/>
          </Stack>
          <Stack className="scrollable" overflow="auto" py="24px" pr="10px" spacing={3}>
            {executions.map((execution, index) => (
              <ExecutionCard
                key={execution.id ?? `execution ${index}`}
                handleDeleteExecution={index !== 0 ? handleDeleteExecution(index) : undefined}
                isMainExecution={index === 0}
                execution={execution}
                readOnly={readOnly}
                updateExecution={updateExecution(index)}
                errors={errors[index]}
              />
            ))}
          </Stack>
          {!readOnly && (
            <Stack alignSelf="flex-end" spacing={3} pb="25px" pt="10px" pr="10px">
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {!isCreation && !!executions.length && (
                  <Button variant="outlined" color="primary" onClick={() => setReminderEmailModalOpen(true)}>
                    {t('operations.executions.manage.button.mailReminder')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || checkIfErrors(errors) || !executions.length}
                  onClick={handleSave}
                >
                  {t('global.save')}
                </Button>
              </Stack>
            </Stack>
          )}
          {reminderEmailModalOpen && (
            <ReminderEmailModal handleClose={() => setReminderEmailModalOpen(false)}/>
          )}
        </Stack>
      </Drawer>
      <Outlet/>
    </>
  );
};

export default ManageExecutionDrawer;
